import React, {useEffect, useState} from 'react';

import {
    Cancel as CancelIcon,
    Save as SaveIcon} from '@mui/icons-material';
import {IconButton, InputBase, Stack} from '@mui/material';

import {v4 as uuidv4} from 'uuid';

import {clear} from 'canvas/canvas-helper';
import {useCanvasStore} from 'canvas/zustand';
import {getKeyboardOptions} from 'components';
import {useTranslation} from 'hooks';
import {useKeyboardStore, usePrefsStore, useSnackbarStore} from 'store';

import {useSheetStore} from './zustand';

const styles = {
    inputBase: {
        backgroundColor: 'white.main',
        fontSize: '16px',
        borderRadius: '0',
        padding: '0 0 0 10px',
        flexGrow: 1,
        height: '50px',
        boxSizing: 'border-box',
        color: 'grey.darkest',
        border: 'none',
        outline: 'none',
        borderBottomLeftRadius: '6px',
        borderTopLeftRadius: '6px',
    },
    button: {
        backgroundColor: 'green.main',
        width: '50px',
        fontSize: '20px',
        height: '50px',
        padding: '0 10px',
        borderRadius: 0,
        margin: 0,

        '&:hover': {
            backgroundColor: 'green.main',
        },

        '&.MuiIconButton-edgeEnd': {
            borderBottomRightRadius: '6px',
            borderTopRightRadius: '6px',
        }
    },
    buttonCancel: {
        backgroundColor: 'white.main',
        color: 'black.main',
        '&:hover': {
            color: 'grey.lightest',
            backgroundColor: 'white.main',
        },
    },
};

export const Sheet = ({sheet, groupId, onSave, onCancel}) => {
    const translate = useTranslation();

    const [name, setName] = useState(sheet?.name || '');
    const [showCancel, setShowCancel] = useState(false);

    const showInfo = useSnackbarStore((state) => state.show);

    const showKeyboard = useKeyboardStore((state) => state.show);
    const hideKeyboard = useKeyboardStore((state) => state.hide);

    const updateSheet = useSheetStore((store) => store.upd);
    const addSheet = useSheetStore((store) => store.add);

    const canvas = useCanvasStore((store) => store.canvas);
    const saveCanvas = useCanvasStore((store) => store.save);

    const background = usePrefsStore((state) => state.background);

    const onInputFocus = (e, setVal) => {
        setShowCancel(true);
        showKeyboard(getKeyboardOptions(e, setVal));
    };

    const onInputChange = (e) => {
        setName(e.target.value);
        setShowCancel(true);
    };

    const onInputBlur = () => {
        hideKeyboard();
    };

    const doCancel = () => {
        setName(sheet?.name || '');
        setShowCancel(false);
        onCancel && onCancel();
    };

    const doSave = () => {
        if (!name || name === '') {
            showInfo(translate('board.sheet.validation.name_required'), {severity: 'warning'});
            return;
        }

        if (sheet === null) {
            const newId = uuidv4();
            const newSheet = {id: newId, name, group: groupId};
            clear(canvas, background);
            saveCanvas(newSheet.id, {background});
            addSheet(newSheet);
            onSave(newSheet);
            showInfo(translate('board.sheet.action.adding_success'));
        } else {
            const updatedSheet = {...sheet, name};
            updateSheet(updatedSheet);
            onSave(updatedSheet);
            showInfo(translate('board.sheet.action.save_success'));
        }

        setName('');
        setShowCancel(false);
    };

    useEffect(() => {
        return () => {
            hideKeyboard();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    {/* editSheet */}

    return <Stack direction="row">
        <InputBase
            sx={styles.inputBase}
            placeholder={translate('board.sheet.name')}
            label=""
            value={name}
            autoComplete="off"
            onFocus={(e) => {
                onInputFocus(e, setName);
            }}
            onKeyPress={(ev) => {
                if (ev.key === 'Enter') {
                    doSave();
                    ev.preventDefault();
                }
            }}
            onBlur={onInputBlur}
            onChange={onInputChange}
        />
        {showCancel && <IconButton sx={[styles.button,styles.buttonCancel]} onClick={doCancel} color="secondary">
            <CancelIcon/>
        </IconButton>}
        <IconButton sx={styles.button} onClick={doSave} edge={'end'} color="secondary">
            <SaveIcon/>
        </IconButton>
    </Stack>;
};

export default Sheet;
